// ============================================================================
// 📦 VERSION: 2026.01.30.1645
// ============================================================================
// JoinQuiz.jsx - Full-featured Join Quiz Page
// Features: 250+ avatars, teams, certificate delivery options
// Code Format: XX-XXXX (e.g., A1-2B3C) - all alphanumeric, hyphen at position 3
// Routes: /group-quiz/join or /group-quiz/join/:code
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = 'https://api.traktag.com/api';

// ============================================================================
// AVATAR DATA - 250+ Categorized Emojis
// ============================================================================
const AVATAR_CATEGORIES = {
  faces: {
    icon: '😀',
    label: 'Faces',
    emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬']
  },
  animals: {
    icon: '🦊',
    label: 'Animals',
    emojis: ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜','🦟','🦗','🕷','🦂','🐢','🐍','🦎','🦖','🦕','🐙','🦑','🦐','🦞','🦀','🐡','🐠','🐟','🐬','🐳','🐋','🦈','🐊','🐅','🐆','🦓','🦍','🦧','🐘','🦛','🦏','🐪','🐫','🦒','🦘','🐃','🐂','🐄','🐎','🐖','🐏','🐑','🦙','🐐','🦌','🐕','🐩','🦮','🐈','🐓','🦃','🦚','🦜','🦢','🦩','🐇','🦝','🦨','🦡','🦫','🦦','🦥','🐁','🐀','🐿','🦔']
  },
  festive: {
    icon: '🎄',
    label: 'Festive',
    emojis: ['🎅','🤶','🧑‍🎄','🧝','🧝‍♂️','🧝‍♀️','⛄','☃️','❄️','🎄','🎁','🦌','🔔','🕯️','✨','🎆','🎇','🧨','🎀','🎊','🎉','🪅','🎃','👻','💀','☠️','👽','🤖','🎭','🥚','🐰','🐣','🪺','💝','💘','💖','💗','💓','💕','❤️','🧡','💛','💚','💙','💜','🤎','🖤','🤍','💯','🔥','⭐','🌟','💫','🌈','🦃','🍂','🍁','🎪']
  },
  fantasy: {
    icon: '🦄',
    label: 'Fantasy',
    emojis: ['🦄','🐉','🐲','🧙','🧙‍♂️','🧙‍♀️','🧚','🧚‍♂️','🧚‍♀️','🧛','🧛‍♂️','🧛‍♀️','🧜','🧜‍♂️','🧜‍♀️','🧝','🧝‍♂️','🧝‍♀️','🧞','🧞‍♂️','🧞‍♀️','🧟','🧟‍♂️','🧟‍♀️','🦸','🦸‍♂️','🦸‍♀️','🦹','🦹‍♂️','🦹‍♀️','🥷','👸','🤴','👼','🎅','🤶','🧑‍🎄','👽','🤖','👾','👻','💀','☠️','👹','👺','🤡','💩','😺','😸','😹','😻','😼','😽','🙀','😿','😾','🎃']
  },
  food: {
    icon: '🍕',
    label: 'Food',
    emojis: ['🍕','🍔','🍟','🌭','🍿','🥓','🥚','🧇','🥞','🍳','🥗','🥪','🌮','🌯','🥙','🍖','🍗','🥩','🍝','🍜','🍲','🍛','🍣','🍱','🥟','🦪','🍤','🍙','🍚','🍘','🍥','🥠','🥮','🍢','🍡','🍧','🍨','🍦','🥧','🧁','🍰','🎂','🍮','🍭','🍬','🍫','🍩','🍪','🌰','🥜','🍯','🥛','🍼','🫖','☕','🍵','🧃','🥤','🧋','🍶','🍺','🍻','🥂','🍷','🥃','🍸','🍹','🧉','🍾','🧊','🥄','🍴','🍽','🥣','🥡','🥢']
  },
  sports: {
    icon: '⚽',
    label: 'Sports',
    emojis: ['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🏓','🏸','🏒','🥅','⛳','🏹','🎣','🤿','🥊','🥋','🎿','⛷','🏂','🪂','🏋️','🏋️‍♂️','🏋️‍♀️','🤼','🤼‍♂️','🤼‍♀️','🤸','🤸‍♂️','🤸‍♀️','⛹️','⛹️‍♂️','⛹️‍♀️','🤺','🏇','⛸','🏌️','🏌️‍♂️','🏌️‍♀️','🏄','🏄‍♂️','🏄‍♀️','🏊','🏊‍♂️','🏊‍♀️','🚣','🚣‍♂️','🚣‍♀️','🧗','🧗‍♂️','🧗‍♀️','🚴','🚴‍♂️','🚴‍♀️','🚵','🚵‍♂️','🚵‍♀️','🏆','🥇','🥈','🥉','🏅','🎖','🎗']
  },
  objects: {
    icon: '🎮',
    label: 'Objects',
    emojis: ['🎮','🕹️','🎲','🎯','🎨','🎭','🎪','🎠','🎡','🎢','🚀','✈️','🛩','🚁','🛸','🚂','🚃','🚄','🚅','🚆','🚇','🚈','🚉','🚊','🚝','🚞','🚋','🚌','🚍','🚎','🚐','🚑','🚒','🚓','🚔','🚕','🚖','🚗','🚘','🚙','🛻','🚚','🚛','🚜','🏎','🏍','🛵','🦽','🦼','🛺','🚲','🛴','🛹','🛼','🚏','🛣','🛤','🛢','⛽','🚨','🚥','🚦','🛑','🚧','⚓','⛵','🛶','🚤','🛳','⛴','🛥','🚢','💎','🔮','🎸','🎹','🎺','🎻','🪕','🥁','🎤','🎧','📱','💻','🖥','⌨️','🖱','🖨','📷','📹','🎥','📽','🎬','📺','📻','🔭','🔬','💡','🔦','🏮','📔','📕','📖','📗','📘','📙','📚']
  }
};

// ============================================================================
// DICEBEAR AVATAR STYLES
// ============================================================================
const DICEBEAR_STYLES = [
  { id: 'adventurer', label: 'Adventurer' },
  { id: 'avataaars', label: 'Classic' },
  { id: 'bottts', label: 'Robot' },
  { id: 'pixel-art', label: 'Pixel' },
  { id: 'lorelei', label: 'Artistic' },
  { id: 'thumbs', label: 'Thumbs' }
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const JoinQuiz = ({
  playerData,
  adminSettings: propAdminSettings,
  onJoinSuccess
}) => {
  const { code: urlCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get code from URL (path or query param)
  const initialCode = urlCode || searchParams.get('code') || '';

  // =========================================================================
  // STATE
  // =========================================================================
  const [step, setStep] = useState(initialCode ? 2 : 1); // 1=enter code, 2=setup profile
  const [quizCode, setQuizCode] = useState(initialCode.toUpperCase());
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValid, setCodeValid] = useState(initialCode ? null : false);
  const [quizInfo, setQuizInfo] = useState(null);
  
  // Profile setup
  const [displayName, setDisplayName] = useState(playerData?.name || '');
  const [selectedAvatar, setSelectedAvatar] = useState('😎');
  const [avatarCategory, setAvatarCategory] = useState('faces');
  const [dicebearStyle, setDicebearStyle] = useState('adventurer');
  const [dicebearSeed, setDicebearSeed] = useState(Date.now().toString());
  const [useDicebear, setUseDicebear] = useState(false);
  
  // Team
  const [joinTeam, setJoinTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  
  // Certificate delivery
  const [certToAccount, setCertToAccount] = useState(true);
  const [certToEmail, setCertToEmail] = useState(false);
  const [certToWhatsApp, setCertToWhatsApp] = useState(false);
  const [certEmail, setCertEmail] = useState(playerData?.email || '');
  const [certPhone, setCertPhone] = useState('');
  
  // UI State
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  // Admin settings
  const [adminSettings, setAdminSettings] = useState({
    adsEnabled: true,
    adPages: { join: true },
    debugMode: true,
    ...propAdminSettings
  });

  // =========================================================================
  // LOAD ADMIN SETTINGS
  // =========================================================================
  useEffect(() => {
    const loadAdminSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/admin/settings`);
        if (response.ok) {
          const data = await response.json();
          setAdminSettings(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        const stored = localStorage.getItem('traktag_admin_settings');
        if (stored) {
          setAdminSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
        }
      }
    };
    loadAdminSettings();
  }, []);

  // =========================================================================
  // VALIDATE CODE ON INITIAL LOAD (if code in URL)
  // =========================================================================
  useEffect(() => {
    if (initialCode) {
      validateQuizCode(initialCode.toUpperCase());
    }
  }, [initialCode]);

  // =========================================================================
  // VALIDATE QUIZ CODE - Format: XX-XXXX (all alphanumeric, e.g., A1-2B3C)
  // =========================================================================
  const validateQuizCode = useCallback(async (code) => {
    // Check format: 7 chars, hyphen at position 2
    if (!code || code.length !== 7 || code[2] !== '-') {
      setCodeValid(false);
      setQuizInfo(null);
      return;
    }

    setValidatingCode(true);
    setError(null);

    try {
      // Try API first
      const response = await fetch(`${API_URL}/group-quiz/${code}/info`);
      
      if (response.ok) {
        const data = await response.json();
        setQuizInfo(data);
        setCodeValid(true);
        if (step === 1) setStep(2);
      } else {
        // Fallback to localStorage for testing
        const stored = localStorage.getItem(`quiz_${code}`);
        if (stored) {
          const data = JSON.parse(stored);
          setQuizInfo(data);
          setCodeValid(true);
          if (step === 1) setStep(2);
        } else {
          setCodeValid(false);
          setQuizInfo(null);
          setError('Quiz not found. Check your code and try again.');
        }
      }
    } catch (err) {
      // Fallback to localStorage
      const stored = localStorage.getItem(`quiz_${code}`);
      if (stored) {
        const data = JSON.parse(stored);
        setQuizInfo(data);
        setCodeValid(true);
        if (step === 1) setStep(2);
      } else {
        setCodeValid(false);
        setError('Quiz not found. Check your code and try again.');
      }
    } finally {
      setValidatingCode(false);
    }
  }, [step]);

  // =========================================================================
  // HANDLE CODE INPUT - Format: XX-XXXX (all alphanumeric, e.g., A1-2B3C)
  // =========================================================================
  const handleCodeChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    
    // Auto-insert hyphen after 2 characters
    if (value.length === 2 && !value.includes('-')) {
      value = value + '-';
    }
    // Remove extra hyphens (only one allowed at position 2)
    if (value.length > 3) {
      const parts = value.split('-');
      if (parts.length > 2) {
        value = parts[0] + '-' + parts.slice(1).join('');
      }
    }
    // Limit to 7 characters (XX-XXXX)
    value = value.slice(0, 7);
    
    setQuizCode(value);
    setError(null);
    
    // Auto-validate when code is complete (7 chars: XX-XXXX)
    if (value.length === 7 && value[2] === '-') {
      validateQuizCode(value);
    } else {
      setCodeValid(false);
      setQuizInfo(null);
    }
  };

  // =========================================================================
  // HANDLE JOIN
  // =========================================================================
  const handleJoin = async () => {
    if (!displayName.trim()) {
      setError('Please enter your display name');
      return;
    }

    setJoining(true);
    setError(null);

    const participantData = {
      username: displayName.trim(),
      displayName: displayName.trim(),
      avatar: useDicebear ? `dicebear:${dicebearStyle}:${dicebearSeed}` : selectedAvatar,
      team: joinTeam ? teamName.trim() : null,
      certToAccount,
      certToEmail: certToEmail ? certEmail : null,
      certToWhatsApp: certToWhatsApp ? certPhone : null,
      joinedAt: new Date().toISOString()
    };

    try {
      // Try API first
      const response = await fetch(`${API_URL}/group-quiz/${quizCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(participantData)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (onJoinSuccess) {
          onJoinSuccess(quizCode, data);
        } else {
          navigate(`/group-quiz/lobby/${quizCode}?user=${encodeURIComponent(displayName)}&pid=${data.participantId || ''}`);
        }
      } else {
        // Fallback to localStorage
        saveToLocalStorage(participantData);
      }
    } catch (err) {
      // Fallback to localStorage
      saveToLocalStorage(participantData);
    } finally {
      setJoining(false);
    }
  };

  const saveToLocalStorage = (participantData) => {
    // Save to localStorage for testing
    const participants = JSON.parse(localStorage.getItem(`participants_${quizCode}`) || '[]');
    participants.push(participantData);
    localStorage.setItem(`participants_${quizCode}`, JSON.stringify(participants));
    
    if (onJoinSuccess) {
      onJoinSuccess(quizCode, participantData);
    } else {
      navigate(`/group-quiz/lobby/${quizCode}?user=${encodeURIComponent(displayName)}`);
    }
  };

  // =========================================================================
  // RANDOM AVATAR
  // =========================================================================
  const pickRandomAvatar = () => {
    const emojis = AVATAR_CATEGORIES[avatarCategory].emojis;
    const random = emojis[Math.floor(Math.random() * emojis.length)];
    setSelectedAvatar(random);
    setUseDicebear(false);
  };

  const regenerateDicebear = () => {
    setDicebearSeed(Date.now().toString() + Math.random().toString(36));
  };

  // =========================================================================
  // DERIVED STATE
  // =========================================================================
  const showAds = adminSettings.adsEnabled && adminSettings.adPages?.join;
  const dicebearUrl = `https://api.dicebear.com/7.x/${dicebearStyle}/svg?seed=${dicebearSeed}`;

  // =========================================================================
  // RENDER - STEP 1: ENTER CODE
  // =========================================================================
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 p-4">
        {/* Top Ad */}
        {showAds && (
          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 rounded-lg p-2">
              <div className="bg-gray-200 rounded w-[320px] h-[50px] flex items-center justify-center text-gray-500 text-sm">
                Ad 320×50
              </div>
            </div>
          </div>
        )}

        <div className="max-w-[400px] mx-auto bg-white rounded-2xl p-6 shadow-xl">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎯</div>
            <h1 className="text-2xl font-bold text-gray-800">Join Quiz</h1>
            <p className="text-gray-500 mt-2">Enter the code from your quiz host</p>
          </div>

          {/* Code Input */}
          <div className="mb-6">
            <input
              type="text"
              value={quizCode}
              onChange={handleCodeChange}
              placeholder="A1-2B3C"
              maxLength={7}
              className="w-full p-4 text-center text-2xl font-bold tracking-widest rounded-xl uppercase border-3 border-gray-300 focus:outline-none focus:border-purple-500 bg-white"
              autoFocus
            />
            
            {validatingCode && (
              <p className="text-center text-gray-500 mt-2">Checking code...</p>
            )}
            
            {codeValid && quizInfo && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border-2 border-green-400">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{quizInfo.icon || '🎪'}</span>
                  <div>
                    <p className="font-bold text-green-700">{quizInfo.title || 'Quiz Found!'}</p>
                    <p className="text-sm text-green-600">
                      {quizInfo.questionCount || '?'} questions • {quizInfo.participantCount || 0} players
                    </p>
                  </div>
                  <span className="ml-auto text-2xl">✅</span>
                </div>
              </div>
            )}
            
            {error && (
              <p className="text-center text-red-500 mt-2">{error}</p>
            )}
          </div>

          {/* Continue Button */}
          <button
            onClick={() => validateQuizCode(quizCode)}
            disabled={!quizCode || quizCode.length !== 7 || quizCode[2] !== '-' || validatingCode}
            className={`
              w-full py-4 rounded-full font-bold text-lg transition-all
              ${quizCode.length === 7 && quizCode[2] === '-' && !validatingCode
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'}
            `}
          >
            {validatingCode ? '⏳ Checking...' : '🎮 Join Quiz'}
          </button>

          {/* Or paste link hint */}
          <p className="text-center text-gray-400 text-sm mt-4">
            Or paste the full quiz link
          </p>

          {/* App promo */}
          <div className="mt-6 p-4 bg-green-50 rounded-xl text-center" style={{ border: '2px solid #22c55e' }}>
            <p className="font-bold text-green-700 mb-1">📱 Better in the App!</p>
            <p className="text-sm text-green-600">Push notifications, haptic feedback & more</p>
          </div>
        </div>

        {/* Version Badge */}
        {adminSettings.debugMode && (
          <div className="fixed bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            JoinQuiz.jsx v2026.01.30.1645
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // RENDER - STEP 2: PROFILE SETUP
  // =========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 p-4">
      {/* Top Ad */}
      {showAds && (
        <div className="flex justify-center mb-4">
          <div className="bg-gray-100 rounded-lg p-2">
            <div className="bg-gray-200 rounded w-[320px] h-[50px] flex items-center justify-center text-gray-500 text-sm">
              Ad 320×50
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[500px] mx-auto bg-white rounded-2xl p-6 shadow-xl">
        {/* Quiz Info Header */}
        {quizInfo && (
          <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl mb-5">
            <span className="text-3xl">{quizInfo.icon || '🎪'}</span>
            <div className="flex-1">
              <p className="font-bold text-purple-800">{quizInfo.title || 'Group Quiz'}</p>
              <p className="text-xs text-purple-600">
                {quizInfo.questionCount || '?'} questions • {quizInfo.participantCount || 0} players waiting
              </p>
            </div>
            <button
              onClick={() => { setStep(1); setCodeValid(false); setQuizInfo(null); }}
              className="text-purple-500 text-sm"
            >
              Change
            </button>
          </div>
        )}

        {/* Display Name */}
        <div className="mb-5">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Your Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your name"
            maxLength={20}
            className="w-full p-3 rounded-xl border-2 border-gray-300 focus:outline-none focus:border-purple-500 bg-white"
          />
        </div>

        {/* Avatar Selection */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700">Choose Avatar</label>
            <button
              onClick={pickRandomAvatar}
              className="text-purple-600 text-sm font-semibold"
            >
              🎲 Random
            </button>
          </div>

          {/* Current Avatar Preview */}
          <div className="flex items-center gap-4 mb-3">
            <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center text-4xl border-2 border-purple-300">
              {useDicebear ? (
                <img src={dicebearUrl} alt="Avatar" className="w-12 h-12 rounded-lg" />
              ) : (
                selectedAvatar
              )}
            </div>
            <div className="text-sm text-gray-500">
              {useDicebear ? `DiceBear: ${dicebearStyle}` : 'Emoji avatar'}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2 mb-2">
            {Object.entries(AVATAR_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => { setAvatarCategory(key); setUseDicebear(false); }}
                className={`
                  flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-all
                  ${avatarCategory === key && !useDicebear
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
            <button
              onClick={() => setUseDicebear(true)}
              className={`
                flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-all
                ${useDicebear
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              `}
            >
              ✨ Custom
            </button>
          </div>

          {/* Emoji Grid or DiceBear */}
          {!useDicebear ? (
            <div className="grid grid-cols-8 gap-2 max-h-[180px] overflow-y-auto p-2 bg-gray-50 rounded-xl">
              {AVATAR_CATEGORIES[avatarCategory].emojis.map((emoji, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedAvatar(emoji)}
                  className={`
                    text-2xl p-2 rounded-lg transition-all
                    ${selectedAvatar === emoji && !useDicebear
                      ? 'bg-purple-200 ring-2 ring-purple-500'
                      : 'hover:bg-gray-200'}
                  `}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-4 mb-3">
                <img 
                  src={dicebearUrl} 
                  alt="Custom Avatar" 
                  className="w-20 h-20 rounded-xl cursor-pointer border-2 border-purple-300"
                  onClick={() => setUseDicebear(true)}
                />
                <button
                  onClick={regenerateDicebear}
                  className="bg-purple-100 text-purple-700 px-3 py-2 rounded-lg text-sm font-semibold"
                >
                  🔄 New
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {DICEBEAR_STYLES.map(style => (
                  <button
                    key={style.id}
                    onClick={() => setDicebearStyle(style.id)}
                    className={`
                      px-3 py-1 rounded-full text-xs font-medium transition-all
                      ${dicebearStyle === style.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}
                    `}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Team Toggle */}
        {quizInfo?.allowTeams !== false && (
          <div className="mb-5">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="font-semibold text-gray-700">Join a Team</p>
                <p className="text-xs text-gray-500">Compete together with friends</p>
              </div>
              <button
                onClick={() => setJoinTeam(!joinTeam)}
                className={`
                  w-12 h-6 rounded-full transition-colors relative
                  ${joinTeam ? 'bg-purple-600' : 'bg-gray-300'}
                `}
              >
                <span className={`
                  absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all
                  ${joinTeam ? 'left-6' : 'left-0.5'}
                `} />
              </button>
            </div>
            
            {joinTeam && (
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Team name"
                maxLength={30}
                className="w-full mt-2 p-3 rounded-xl border-2 border-gray-300 focus:outline-none focus:border-purple-500 bg-white"
              />
            )}
          </div>
        )}

        {/* Certificate Delivery */}
        <div className="mb-5">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            📜 Certificate Delivery
          </label>
          <p className="text-xs text-gray-500 mb-3">Where should we send your quiz certificate?</p>
          
          <div className="space-y-2">
            {/* TRAKtag Account */}
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={certToAccount}
                onChange={(e) => setCertToAccount(e.target.checked)}
                className="w-5 h-5 accent-purple-600"
              />
              <div>
                <p className="font-medium text-gray-700">👤 TRAKtag Account</p>
                <p className="text-xs text-gray-500">Saved to your profile</p>
              </div>
            </label>

            {/* Email */}
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={certToEmail}
                onChange={(e) => setCertToEmail(e.target.checked)}
                className="w-5 h-5 accent-purple-600"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-700">📧 Email</p>
                {certToEmail && (
                  <input
                    type="email"
                    value={certEmail}
                    onChange={(e) => setCertEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 bg-white"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            </label>

            {/* WhatsApp */}
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={certToWhatsApp}
                onChange={(e) => setCertToWhatsApp(e.target.checked)}
                className="w-5 h-5 accent-purple-600"
              />
              <div className="flex-1">
                <p className="font-medium text-gray-700">💬 WhatsApp</p>
                {certToWhatsApp && (
                  <input
                    type="tel"
                    value={certPhone}
                    onChange={(e) => setCertPhone(e.target.value)}
                    placeholder="+44 7700 900123"
                    className="w-full mt-1 p-2 text-sm rounded-lg border border-gray-300 bg-white"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p className="text-center text-red-500 mb-4">{error}</p>
        )}

        {/* Join Button */}
        <button
          onClick={handleJoin}
          disabled={!displayName.trim() || joining}
          className={`
            w-full py-4 rounded-full font-bold text-lg transition-all
            ${displayName.trim() && !joining
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'}
          `}
        >
          {joining ? '⏳ Joining...' : '🎮 Join Quiz'}
        </button>

        {/* Back link */}
        <p className="text-center mt-4">
          <button
            onClick={() => { setStep(1); setCodeValid(false); setQuizInfo(null); }}
            className="text-purple-600 text-sm"
          >
            ← Enter different code
          </button>
        </p>
      </div>

      {/* Bottom Ad */}
      {showAds && (
        <div className="flex justify-center mt-4">
          <div className="bg-gray-100 rounded-lg p-2">
            <div className="bg-gray-200 rounded w-[300px] h-[250px] flex items-center justify-center text-gray-500 text-sm">
              Ad 300×250
            </div>
          </div>
        </div>
      )}

      {/* Version Badge */}
      {adminSettings.debugMode && (
        <div className="fixed bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          JoinQuiz.jsx v2026.01.30.1645
        </div>
      )}
    </div>
  );
};

export default JoinQuiz;
